import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Send, Plus, Search, Filter, Eye, RefreshCw, Trash2,
  Mail, Clock, UserPlus, CheckCircle, XCircle, Timer,
  Building2, TrendingUp, Calendar, Download, ChevronDown,
  Database, Users, Shield, AlertCircle, Copy, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import invitationService from '../services/invitationService';
import dataroomService from '../services/dataroomService';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import ResponsiveModal, { 
  ResponsiveForm, 
  ResponsiveInput, 
  ResponsiveButton
} from '../components/ResponsiveModal';
import { PageLoader } from '../components/CommonLoader';

const AllInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [datarooms, setDatarooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDataroom, setFilterDataroom] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [expandedDataroom, setExpandedDataroom] = useState(null);

  // Statistics
  const [globalStats, setGlobalStats] = useState({
    totalInvitations: 0,
    totalPending: 0,
    totalAccepted: 0,
    totalRejected: 0,
    totalExpired: 0,
    globalAcceptanceRate: 0,
    totalDatarooms: 0,
    activeDatarooms: 0,
    topDatarooms: []
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAllInvitations();
    fetchDatarooms();
  }, [searchTerm, filterStatus, filterDataroom, dateRange]);

  useEffect(() => {
    calculateGlobalStats();
  }, [invitations]);

  const fetchAllInvitations = async () => {
    try {
      setLoading(true);
      
      // Build params for API call
      const params = {
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterDataroom && { dataroom_uuid: filterDataroom }),
        ...(dateRange.start && { start_date: dateRange.start }),
        ...(dateRange.end && { end_date: dateRange.end })
      };

      // Try to fetch from admin API first
      try {
        const response = await invitationService.admin.listAll(params);
        if (response.data) {
          setInvitations(response.data.data || response.data || []);
        }
      } catch (adminError) {
        
        // Use sample data if API is not available
        const sampleData = [
          {
            id: 1,
            email: 'john.doe@example.com',
            dataroom: { uuid: 'dr-001', name: 'Q4 Financial Reports' },
            role: { display_name: 'Viewer' },
            status: 'pending',
            created_at: '2024-01-15T10:00:00Z',
            expires_at: '2024-02-15T10:00:00Z',
            invitation_link: 'https://lockbox.com/invite/abc123'
          },
          {
            id: 2,
            email: 'jane.smith@example.com',
            dataroom: { uuid: 'dr-002', name: 'M&A Documents' },
            role: { display_name: 'Editor' },
            status: 'accepted',
            created_at: '2024-01-10T10:00:00Z',
            accepted_at: '2024-01-11T14:30:00Z'
          },
          {
            id: 3,
            email: 'mike.wilson@example.com',
            dataroom: { uuid: 'dr-001', name: 'Q4 Financial Reports' },
            role: { display_name: 'Viewer' },
            status: 'expired',
            created_at: '2023-12-01T10:00:00Z',
            expires_at: '2024-01-01T10:00:00Z'
          },
          {
            id: 4,
            email: 'sarah.johnson@example.com',
            dataroom: { uuid: 'dr-003', name: 'Legal Contracts' },
            role: { display_name: 'Admin' },
            status: 'accepted',
            created_at: '2024-01-12T10:00:00Z',
            accepted_at: '2024-01-12T15:00:00Z'
          },
          {
            id: 5,
            email: 'robert.brown@example.com',
            dataroom: { uuid: 'dr-002', name: 'M&A Documents' },
            role: { display_name: 'Viewer' },
            status: 'pending',
            created_at: '2024-01-14T10:00:00Z',
            expires_at: '2024-02-14T10:00:00Z'
          },
          {
            id: 6,
            email: 'emily.davis@example.com',
            dataroom: { uuid: 'dr-004', name: 'Product Roadmap' },
            role: { display_name: 'Editor' },
            status: 'pending',
            created_at: '2024-01-16T10:00:00Z',
            expires_at: '2024-02-16T10:00:00Z'
          },
          {
            id: 7,
            email: 'michael.taylor@example.com',
            dataroom: { uuid: 'dr-003', name: 'Legal Contracts' },
            role: { display_name: 'Viewer' },
            status: 'rejected',
            created_at: '2024-01-05T10:00:00Z',
            rejected_at: '2024-01-06T10:00:00Z'
          },
          {
            id: 8,
            email: 'lisa.anderson@example.com',
            dataroom: { uuid: 'dr-005', name: 'HR Policies' },
            role: { display_name: 'Admin' },
            status: 'accepted',
            created_at: '2024-01-08T10:00:00Z',
            accepted_at: '2024-01-08T14:00:00Z'
          }
        ];

        // Apply filters
        let filteredData = sampleData;
        
        if (searchTerm) {
          filteredData = filteredData.filter(inv => 
            inv.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.dataroom.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        if (filterStatus) {
          filteredData = filteredData.filter(inv => inv.status === filterStatus);
        }
        
        if (filterDataroom) {
          filteredData = filteredData.filter(inv => inv.dataroom.uuid === filterDataroom);
        }

        setInvitations(filteredData);
      }
    } catch (error) {
      toast.error('Failed to fetch invitations');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDatarooms = async () => {
    try {
      // Try to fetch from super admin API
      const response = await dataroomService.getAllDatarooms({ per_page: 100 });
      if (response.datarooms) {
        const dataroomsData = response.datarooms.map(dr => ({
          uuid: dr.uuid,
          name: dr.name,
          invitation_count: dr.invitation_count || 0
        }));
        setDatarooms(dataroomsData);
      }
    } catch (error) {
      // Use sample datarooms if API fails
      const sampleDatarooms = [
        { uuid: 'dr-001', name: 'Q4 Financial Reports', invitation_count: 25 },
        { uuid: 'dr-002', name: 'M&A Documents', invitation_count: 18 },
        { uuid: 'dr-003', name: 'Legal Contracts', invitation_count: 12 },
        { uuid: 'dr-004', name: 'Product Roadmap', invitation_count: 8 },
        { uuid: 'dr-005', name: 'HR Policies', invitation_count: 5 }
      ];
      setDatarooms(sampleDatarooms);
    }
  };

  const calculateGlobalStats = () => {
    const total = invitations.length;
    const pending = invitations.filter(inv => inv.status === 'pending').length;
    const accepted = invitations.filter(inv => inv.status === 'accepted').length;
    const rejected = invitations.filter(inv => inv.status === 'rejected').length;
    const expired = invitations.filter(inv => inv.status === 'expired').length;
    const acceptanceRate = total > 0 ? Math.round((accepted / total) * 100) : 0;

    // Group by dataroom for top datarooms
    const dataroomCounts = invitations.reduce((acc, inv) => {
      const key = inv.dataroom.name;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const topDatarooms = Object.entries(dataroomCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([name, count]) => ({ name, count }));

    setGlobalStats({
      totalInvitations: total,
      totalPending: pending,
      totalAccepted: accepted,
      totalRejected: rejected,
      totalExpired: expired,
      globalAcceptanceRate: acceptanceRate,
      totalDatarooms: datarooms.length,
      activeDatarooms: Object.keys(dataroomCounts).length,
      topDatarooms
    });
  };

  const handleResend = async (invitation) => {
    try {
      // Try admin API first
      try {
        await invitationService.admin.resend(invitation.id);
      } catch {
        // Fallback to regular API
        await invitationService.resend(invitation.dataroom.uuid, invitation.id);
      }
      toast.success('Invitation resent successfully');
      fetchAllInvitations();
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleCopyLink = (invitation) => {
    if (invitation.invitation_link) {
      navigator.clipboard.writeText(invitation.invitation_link);
      toast.success('Invitation link copied to clipboard');
    }
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      
      // Try admin API first
      try {
        await invitationService.admin.delete(selectedInvitation.id);
      } catch {
        // Fallback to regular API
        await invitationService.delete(selectedInvitation.dataroom.uuid, selectedInvitation.id);
      }
      
      toast.success('Invitation deleted successfully');
      setShowDeleteModal(false);
      setSelectedInvitation(null);
      fetchAllInvitations();
    } catch (error) {
      toast.error('Failed to delete invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExport = (format) => {
    // Simulate export functionality
    toast.success(`Exporting invitations as ${format}...`);
    
    // In a real implementation, you would:
    // 1. Call an API endpoint to generate the export
    // 2. Download the generated file
    
    // For now, we'll create a simple CSV export
    if (format === 'csv') {
      const csvContent = [
        ['Email', 'Dataroom', 'Role', 'Status', 'Created At', 'Expires At'],
        ...invitations.map(inv => [
          inv.email,
          inv.dataroom.name,
          inv.role?.display_name || 'N/A',
          inv.status,
          new Date(inv.created_at).toISOString(),
          inv.expires_at ? new Date(inv.expires_at).toISOString() : 'Never'
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invitations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    
    setShowExportModal(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: Clock,
        label: 'Pending'
      },
      accepted: {
        color: 'bg-green-50 text-green-700 border-green-200',
        icon: CheckCircle,
        label: 'Accepted'
      },
      rejected: {
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: XCircle,
        label: 'Rejected'
      },
      expired: {
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        icon: Timer,
        label: 'Expired'
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const columns = [
    {
      key: 'recipient',
      label: 'Recipient',
      render: (row) => (
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
              {row.email.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {row.email}
            </p>
            <p className="text-xs text-gray-500 flex items-center mt-1">
              <Shield className="w-3 h-3 mr-1" />
              {row.role?.display_name || 'N/A'}
            </p>
          </div>
        </div>
      )
    },
    {
      key: 'dataroom',
      label: 'Dataroom',
      render: (row) => (
        <div className="flex items-center">
          <Database className="w-4 h-4 text-gray-400 mr-2" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {row.dataroom.name}
            </p>
            <p className="text-xs text-gray-500">
              ID: {row.dataroom.uuid}
            </p>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div className="flex flex-col items-start">
          {getStatusBadge(row.status || 'pending')}
          {row.status === 'accepted' && row.accepted_at && (
            <span className="text-xs text-gray-500 mt-1">
              {new Date(row.accepted_at).toLocaleDateString()}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'sent',
      label: 'Sent',
      render: (row) => (
        <div className="text-sm">
          <p className="text-gray-900">
            {new Date(row.created_at).toLocaleDateString()}
          </p>
          <p className="text-xs text-gray-500">
            {new Date(row.created_at).toLocaleTimeString()}
          </p>
        </div>
      )
    },
    {
      key: 'expires',
      label: 'Expires',
      render: (row) => {
        if (!row.expires_at) {
          return <span className="text-sm text-gray-500">Never</span>;
        }
        
        const expiryDate = new Date(row.expires_at);
        const now = new Date();
        const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        
        return (
          <div className="text-sm">
            <p className="text-gray-900">
              {expiryDate.toLocaleDateString()}
            </p>
            {row.status === 'pending' && daysLeft > 0 && (
              <p className={`text-xs ${daysLeft <= 7 ? 'text-orange-600' : 'text-gray-500'}`}>
                {daysLeft} days left
              </p>
            )}
          </div>
        );
      }
    }
  ];

  const actions = (row) => [
    {
      icon: Eye,
      label: 'View Details',
      onClick: () => {
        setSelectedInvitation(row);
        setShowViewModal(true);
      }
    },
    {
      icon: Copy,
      label: 'Copy Link',
      onClick: () => handleCopyLink(row),
      disabled: row.status !== 'pending' || !row.invitation_link
    },
    {
      icon: RefreshCw,
      label: 'Resend',
      onClick: () => handleResend(row),
      disabled: row.status !== 'pending'
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => {
        setSelectedInvitation(row);
        setShowDeleteModal(true);
      },
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white mr-4">
                  <Send className="w-6 h-6" />
                </div>
                All System Invitations
              </h1>
              <p className="text-gray-600 mt-2 ml-16">
                Centralized management of all dataroom invitations across the system
              </p>
            </div>
            <button
              onClick={() => setShowExportModal(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </button>
          </div>
        </div>

        {/* Global Statistics Dashboard */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-indigo-600" />
            Global Statistics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {/* Main Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {globalStats.totalInvitations}
                  </p>
                </div>
                <Mail className="w-8 h-8 text-blue-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">
                    {globalStats.totalPending}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Accepted</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {globalStats.totalAccepted}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Rejected</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {globalStats.totalRejected}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Expired</p>
                  <p className="text-2xl font-bold text-gray-600 mt-1">
                    {globalStats.totalExpired}
                  </p>
                </div>
                <Timer className="w-8 h-8 text-gray-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-indigo-600 rounded-xl p-4 shadow-sm text-white"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-indigo-100 uppercase tracking-wider">Accept Rate</p>
                  <p className="text-2xl font-bold mt-1">
                    {globalStats.globalAcceptanceRate}%
                  </p>
                </div>
                <UserPlus className="w-8 h-8 text-white/80" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Datarooms</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {globalStats.totalDatarooms}
                  </p>
                </div>
                <Database className="w-8 h-8 text-indigo-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Active</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">
                    {globalStats.activeDatarooms}
                  </p>
                </div>
                <Building2 className="w-8 h-8 text-indigo-500" />
              </div>
            </motion.div>
          </div>

          {/* Top Datarooms */}
          {globalStats.topDatarooms.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Top Datarooms by Invitations
              </h3>
              <div className="space-y-3">
                {globalStats.topDatarooms.map((dataroom, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-600'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {dataroom.name}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {dataroom.count} invitations
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Advanced Filters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
        >
          <div className="flex items-center mb-4">
            <Filter className="w-5 h-5 text-gray-400 mr-2" />
            <h3 className="font-medium text-gray-900">Advanced Filters</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex flex-1 gap-4 flex-wrap">
              {/* Search */}
              <div className="relative flex-1 max-w-md min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search email or dataroom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>

              {/* Dataroom Filter */}
              <select
                value={filterDataroom}
                onChange={(e) => setFilterDataroom(e.target.value)}
                className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="">All Datarooms</option>
                {datarooms.map(dr => (
                  <option key={dr.uuid} value={dr.uuid}>
                    {dr.name}
                  </option>
                ))}
              </select>

              {/* Date Range Filters */}
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Start Date"
              />

              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="End Date"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterDataroom('');
                setDateRange({ start: '', end: '' });
              }}
              className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Clear All Filters
            </button>
          </div>
        </motion.div>

        {/* Invitations Table */}
        {loading ? (
          <PageLoader />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <ResponsiveDataTable
              data={invitations}
              columns={columns}
              actions={actions}
              emptyMessage="No invitations found"
              emptyIcon={Send}
            />
          </motion.div>
        )}

        {/* View Invitation Modal */}
        <ResponsiveModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedInvitation(null);
          }}
          title="Invitation Details"
          icon={Eye}
          maxWidth="max-w-2xl"
        >
          {selectedInvitation && (
            <div className="space-y-4">
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Email</label>
                    <p className="font-medium text-gray-900 mt-1">{selectedInvitation.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Dataroom</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedInvitation.dataroom.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Role</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedInvitation.role?.display_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedInvitation.status || 'pending')}</div>
                  </div>
                </div>
              </div>

              {selectedInvitation.invitation_link && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Invitation Link</label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      value={selectedInvitation.invitation_link}
                      readOnly
                      className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 flex-1 bg-gray-50 text-sm"
                    />
                    <button
                      onClick={() => handleCopyLink(selectedInvitation)}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <a
                      href={selectedInvitation.invitation_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Sent At</label>
                  <p className="font-medium text-gray-900 mt-1">
                    {new Date(selectedInvitation.created_at).toLocaleString()}
                  </p>
                </div>
                {selectedInvitation.expires_at && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Expires At</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {new Date(selectedInvitation.expires_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <ResponsiveButton
                  onClick={() => handleResend(selectedInvitation)}
                  disabled={selectedInvitation.status !== 'pending'}
                  className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Invitation
                </ResponsiveButton>
                <ResponsiveButton
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedInvitation(null);
                  }}
                  className="bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Close
                </ResponsiveButton>
              </div>
            </div>
          )}
        </ResponsiveModal>

        {/* Export Modal */}
        <ResponsiveModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Export Invitations Data"
          icon={Download}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Choose the format to export all invitations data:
            </p>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => handleExport('csv')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-green-600 font-bold">CSV</span>
                  </div>
                  <p className="text-sm font-medium">CSV File</p>
                </div>
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-600 font-bold">XLS</span>
                  </div>
                  <p className="text-sm font-medium">Excel File</p>
                </div>
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-red-600 font-bold">PDF</span>
                  </div>
                  <p className="text-sm font-medium">PDF Report</p>
                </div>
              </button>
            </div>
          </div>
        </ResponsiveModal>

        {/* Delete Confirmation Modal */}
        <ResponsiveModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedInvitation(null);
          }}
          title="Delete Invitation"
          icon={AlertCircle}
          iconColor="text-red-600"
        >
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete this invitation? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <ResponsiveButton
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedInvitation(null);
              }}
              className="bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </ResponsiveButton>
          </div>
        </ResponsiveModal>
      </div>
    </div>
  );
};

export default AllInvitations;